import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { TooltipBreakdownRow } from './CalculationTooltip.tsx';

/*
 * THE FIGURE IS THE TRIGGER (owner, 24 Oct 2026, advice-document review item 8). The year-by-year tables
 * used to put a small circled "?" beside every figure; now the figure itself carries a faint dotted
 * underline and opens its breakdown on hover, keyboard focus or a tap (a second tap, Escape, leaving it
 * or tapping elsewhere closes it). The breakdown is the app's navy panel with a pointer toward the
 * figure, labels on the left and figures right-aligned. Same rows as CalculationTooltip, same words.
 */

const NAVY = '#0F2A4A';
const GAP = 10;
const MARGIN = 16;

/** The dotted underline that marks a figure with a breakdown behind it. */
export const FIGURE_TIP_TRIGGER_STYLE: React.CSSProperties = {
    textDecorationLine: 'underline',
    textDecorationStyle: 'dotted',
    textDecorationColor: '#94a3b8',
    textDecorationThickness: '1.5px',
    textUnderlineOffset: '4px',
    cursor: 'help',
};

/** A row's figure as it is shown: numbers rounded with thousands separators, words as written. */
function shown(value: TooltipBreakdownRow['value']): string | number {
    return typeof value === 'number' && !isNaN(value) ? Math.round(value).toLocaleString() : value;
}

interface FigureTipPanelProps {
    id?: string;
    title: React.ReactNode;
    data: TooltipBreakdownRow[];
    style?: React.CSSProperties;
    /** Where the pointer sits: above the panel (panel below the figure) or below it, and how far across. */
    arrow?: { side: 'top' | 'bottom'; left: number } | null;
    panelRef?: React.Ref<HTMLDivElement>;
}

/** The navy breakdown panel on its own (positioning is the caller's), so it can be rendered in tests. */
export function FigureTipPanel({ id, title, data, style, arrow, panelRef }: FigureTipPanelProps) {
    return (
        <div
            ref={panelRef}
            id={id}
            role="tooltip"
            className="figure-tip-panel"
            style={{
                position: 'relative',
                background: NAVY,
                color: '#ffffff',
                borderRadius: 12,
                boxShadow: '0 12px 32px rgba(15, 42, 74, 0.28), 0 2px 6px rgba(15, 42, 74, 0.18)',
                padding: '12px 14px',
                width: 'max-content',
                minWidth: 240,
                maxWidth: 'min(360px, 90vw)',
                fontSize: 12,
                lineHeight: 1.45,
                ...style,
            }}
        >
            {arrow ? (
                <span
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        left: arrow.left - 5,
                        top: arrow.side === 'top' ? -5 : undefined,
                        bottom: arrow.side === 'bottom' ? -5 : undefined,
                        width: 10,
                        height: 10,
                        background: NAVY,
                        transform: 'rotate(45deg)',
                    }}
                />
            ) : null}
            <div style={{ color: '#7dd3fc', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{title}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', columnGap: 16, rowGap: 4 }}>
                {data.map((item, index) => {
                    const value = shown(item.value);
                    if (item.emphasis) {
                        const isDeficit = item.label === 'Deficit funded from';
                        return (
                            <div
                                key={`emphasis-${item.label}-${index}`}
                                style={{
                                    gridColumn: '1 / -1',
                                    marginTop: 6,
                                    padding: '6px 8px',
                                    borderRadius: 6,
                                    borderTop: '1px solid rgba(255, 255, 255, 0.18)',
                                    background: isDeficit ? 'rgba(127, 29, 29, 0.25)' : 'rgba(20, 83, 45, 0.25)',
                                }}
                            >
                                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#cbd5e1', marginBottom: 2 }}>{item.label}</div>
                                <div style={{ fontWeight: 600, color: isDeficit ? '#fca5a5' : '#86efac' }}>{value}</div>
                            </div>
                        );
                    }
                    if (item.label.startsWith('---')) {
                        return <hr key={`hr-${item.label}-${index}`} style={{ gridColumn: '1 / -1', border: 0, borderTop: '1px solid rgba(255, 255, 255, 0.18)', margin: '4px 0' }} />;
                    }
                    // A sentence in place of a figure (a "Note" row) reads under its label rather than squeezed beside it.
                    if (typeof value === 'string' && value.length > 28 && /[a-z]{3}/i.test(value)) {
                        return (
                            <div key={`row-${item.label}-${index}`} style={{ gridColumn: '1 / -1' }}>
                                <div style={{ color: '#cbd5e1' }}>{item.label}:</div>
                                <div style={{ fontWeight: 600 }}>{value}</div>
                            </div>
                        );
                    }
                    return (
                        <React.Fragment key={`row-${item.label}-${index}`}>
                            <span style={{ color: '#cbd5e1' }}>{item.label}:</span>
                            <span style={{ whiteSpace: 'nowrap', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{value}</span>
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}

interface FigureTipProps {
    title: React.ReactNode;
    data?: TooltipBreakdownRow[];
    /** Colour and font of the figure itself (e.g. `font-mono text-green-600`). */
    className?: string;
    children: React.ReactNode;
}

/** A figure in a year-by-year table whose breakdown opens from the figure itself. */
export default function FigureTip({ title, data, className = '', children }: FigureTipProps) {
    const id = useId();
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLSpanElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    // A tap focuses the figure as well as clicking it; the click decides, so the focus must not open it first.
    const pointerRef = useRef<string | null>(null);
    const [place, setPlace] = useState<{ top: number; left: number; arrow: { side: 'top' | 'bottom'; left: number } } | null>(null);

    const close = useCallback(() => { setOpen(false); setPlace(null); }, []);

    // Below the figure when there is room, otherwise above; kept inside the window with the pointer on the figure.
    useLayoutEffect(() => {
        if (!open || !triggerRef.current || !panelRef.current) return;
        const t = triggerRef.current.getBoundingClientRect();
        const p = panelRef.current.getBoundingClientRect();
        const below = window.innerHeight - t.bottom >= p.height + GAP + MARGIN || t.top < p.height + GAP + MARGIN;
        let top = below ? t.bottom + GAP : t.top - p.height - GAP;
        top = Math.max(MARGIN, Math.min(top, window.innerHeight - p.height - MARGIN));
        const centre = t.left + t.width / 2;
        const left = Math.max(MARGIN, Math.min(centre - p.width / 2, window.innerWidth - p.width - MARGIN));
        const arrowLeft = Math.max(16, Math.min(centre - left, p.width - 16));
        setPlace({ top, left, arrow: { side: below ? 'top' : 'bottom', left: arrowLeft } });
    }, [open]);

    // Escape, or a tap anywhere else, closes it; so does the page scrolling away under it.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
        const onDown = (e: PointerEvent) => {
            const target = e.target as Node | null;
            if (target && (triggerRef.current?.contains(target) || panelRef.current?.contains(target))) return;
            close();
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('pointerdown', onDown);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('pointerdown', onDown);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [open, close]);

    if (!Array.isArray(data)) return <span className={className}>{children}</span>;

    return (
        <>
            <span
                ref={triggerRef}
                tabIndex={0}
                aria-describedby={open ? id : undefined}
                className={`figure-tip ${className}`.trim()}
                style={FIGURE_TIP_TRIGGER_STYLE}
                onPointerEnter={(e) => { if (e.pointerType === 'mouse') setOpen(true); }}
                onPointerLeave={(e) => { if (e.pointerType === 'mouse') close(); }}
                onPointerDown={(e) => { pointerRef.current = e.pointerType; }}
                onFocus={() => { if (!pointerRef.current) setOpen(true); }}
                onBlur={() => { pointerRef.current = null; close(); }}
                onClick={() => {
                    const kind = pointerRef.current;
                    pointerRef.current = null;
                    if (kind && kind !== 'mouse') { if (open) close(); else setOpen(true); }
                }}
                onKeyDown={(e) => { if (e.key === 'Escape') close(); }}
            >
                {children}
            </span>
            {open && typeof document !== 'undefined' &&
                createPortal(
                    <FigureTipPanel
                        id={id}
                        panelRef={panelRef}
                        title={title}
                        data={data}
                        arrow={place?.arrow ?? null}
                        style={{
                            position: 'fixed',
                            top: place?.top ?? 0,
                            left: place?.left ?? 0,
                            zIndex: 10001,
                            // Measured first, then shown where it fits.
                            visibility: place ? 'visible' : 'hidden',
                        }}
                    />,
                    document.body,
                )}
        </>
    );
}
